import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Plus, Trash2, PlusCircle, ListPlus, X, Tag, Globe, Pencil, Search, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import SAR from '../components/SAR';
import { PriceOptionPicker, hasPriceOptions } from '../components/PriceOptionsEditor';
import SearchableCountrySelect from '../components/SearchableCountrySelect';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_COUNTRIES = ['بنجلاديش', 'الهند', 'المغرب', 'باكستان', 'أمريكا', 'بريطانيا', 'كندا', 'أخرى'];

// ─── Saudi-time helpers ──────────────────────────────────────────────────────
// Avoids the UTC-vs-Saudi date-boundary bug where today's sale fails to appear
// in this-week/this-month/this-quarter aggregates because the timestamp lands
// just outside the Saudi-aligned period window.
//
// • saudiToday(): "YYYY-MM-DD" reflecting *Saudi* current date (not UTC).
// • saudiDateToUtcNoon(d): converts a "YYYY-MM-DD" picked by the user (interpreted
//   as a Saudi local date) into an ISO timestamp at 12:00 Saudi noon (= 09:00 UTC).
//   12:00 noon sits safely inside the Saudi day so the timestamp falls within
//   today's daily/weekly/monthly/quarterly windows immediately upon insertion.
const saudiToday = () => {
  // en-CA produces "YYYY-MM-DD" directly
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Riyadh' });
};
// Legacy fallbacks used only until the dynamic /api/coupons list loads.
// Once coupons state is populated the component uses the DB-backed labels.
const COUPON_VALUES = [10, 15, 25, 50, 96, 51];
const COUPON_LABEL_FALLBACK = { 96: 'ND96', 51: 'PD50' };
const couponLabel = (pct, coupons = null) => {
  if (coupons && coupons.length) {
    const found = coupons.find((c) => Number(c.pct) === Number(pct));
    if (found) return found.label;
  }
  return COUPON_LABEL_FALLBACK[pct] || `${pct}%`;
};
// Build a Map<pct(number), label(string)> used by both the mobile and desktop
// sale list cells so each sale badge shows the coupon NAME (PD50, ND96, 10%, …)
// rather than the raw stored sentinel integer.
const buildCouponLabelMap = (coupons) => {
  const m = new Map();
  (coupons || []).forEach((c) => m.set(Number(c.pct), c.label));
  Object.entries(COUPON_LABEL_FALLBACK).forEach(([k, v]) => {
    if (!m.has(Number(k))) m.set(Number(k), v);
  });
  return m;
};

const saudiDateToUtcNoon = (dateStr) => {
  if (!dateStr) return new Date().toISOString();
  // If the picked date is *today in Saudi*, use the CURRENT live moment so the sale
  // is immediately included in today/this-week/this-month aggregates (Saudi-noon
  // would be in the future on a morning-time recording → excluded by `now` upper-bound).
  if (dateStr === saudiToday()) return new Date().toISOString();
  const [Y, M, D] = dateStr.split('-').map(Number);
  // For past/future dates picked explicitly, anchor at Saudi noon (12:00 +03:00 = 09:00 UTC)
  // to safely sit inside the Saudi calendar day regardless of string-compare boundaries.
  return new Date(Date.UTC(Y, M - 1, D, 9, 0, 0)).toISOString();
};

// Quantity picker: quick-select 1..20 dropdown + free typing (unlimited).
// Native <datalist> is unreliable on mobile Safari (the number keyboard covers it),
// so we render a small Popover next to the input with buttons 1..20. The input
// itself still accepts any typed value (e.g. 300) without an upper bound.
// Memoised so it stops re-rendering when unrelated fields on the sale form change.
const QuantityPicker = React.memo(function QuantityPicker({ value, onChange, testId, size = 'md' }) {
  const [open, setOpen] = React.useState(false);
  const heightCls = size === 'sm' ? 'h-9' : 'h-10';
  const iconBtnCls = size === 'sm' ? 'h-9 w-9' : 'h-10 w-10';
  return (
    <div className="flex items-stretch gap-1">
      <Input
        data-testid={testId}
        type="number"
        inputMode="numeric"
        min="1"
        step="1"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className={`text-right font-numbers flex-1 ${heightCls} ${size === 'sm' ? 'text-sm' : ''}`}
        placeholder="اكتب الرقم"
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className={`${iconBtnCls} p-0 flex-shrink-0`}
            data-testid={`${testId}-picker-btn`}
            aria-label="اختر الكمية من القائمة"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[220px] p-2 bg-white"
          align="end"
          dir="rtl"
          data-testid={`${testId}-picker-popover`}
        >
          <p className="text-[11px] text-foreground/55 mb-2 font-bold">اختيار سريع (1 - 20)</p>
          <div className="grid grid-cols-5 gap-1.5">
            {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => {
              const active = String(value) === String(n);
              return (
                <button
                  key={n}
                  type="button"
                  data-testid={`${testId}-picker-opt-${n}`}
                  onClick={() => {
                    onChange(String(n));
                    setOpen(false);
                  }}
                  className={`h-8 rounded-md text-sm font-bold font-numbers border transition-colors ${
                    active
                      ? 'bg-brand_blue text-white border-brand_blue'
                      : 'bg-white text-foreground border-border hover:bg-brand_blue/10 hover:border-brand_blue/40'
                  }`}
                >
                  {n}
                </button>
              );
            })}
          </div>
          <p className="text-[10px] text-foreground/50 mt-2 leading-relaxed">
            لأي رقم أكبر (مثل 300) اكتبه يدوياً في الحقل — بدون حد أقصى.
          </p>
        </PopoverContent>
      </Popover>
    </div>
  );
});

// ─── SalesTables: heavy table (mobile list + desktop table + filter bar) ────
// Memoized so it does NOT re-render on every keystroke of the sale dialog.
// This was the ROOT CAUSE of the typing lag: with ~200+ sales rows, every
// setFormData({ ...formData, customer_name: ... }) forced React to re-run the
// JSX for hundreds of table rows. React.memo + stable props (useState setters
// are stable, useCallback for edit/delete, useMemo-derived filteredSales stays
// referentially equal while typing) fully skips this subtree.
const SalesTables = React.memo(function SalesTables({
  filteredSales,
  salesTotal,
  isEmployee,
  myEmpId,
  onEdit,
  onDelete,
  searchQuery,
  setSearchQuery,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  couponFilter,
  setCouponFilter,
  couponChips,
  couponLabelMap,
}) {
  const labelFor = (pct) => {
    if (pct == null) return 'كود خصم';
    const m = couponLabelMap && couponLabelMap.get ? couponLabelMap.get(Number(pct)) : null;
    return m || `${pct}%`;
  };
  return (
    <Card className="border-border">
      {/* Search + date range */}
      <div className="px-3 sm:px-6 py-3 border-b border-border bg-accent/10 space-y-2" data-testid="sales-search-bar">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-2 items-end">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40 pointer-events-none" />
            <Input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="ابحث باسم الموظف أو المنتج..."
              className="pr-9 h-9 text-sm"
              data-testid="sales-search-input"
            />
          </div>
          <div className="flex flex-col gap-0.5">
            <label className="text-[10px] text-foreground/55 font-bold">من تاريخ</label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-9 text-sm font-numbers"
              data-testid="sales-date-from"
            />
          </div>
          <div className="flex flex-col gap-0.5">
            <label className="text-[10px] text-foreground/55 font-bold">إلى تاريخ</label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-9 text-sm font-numbers"
              data-testid="sales-date-to"
            />
          </div>
          {(searchQuery || dateFrom || dateTo) && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => { setSearchQuery(''); setDateFrom(''); setDateTo(''); }}
              data-testid="sales-search-clear"
              className="h-9 gap-1 text-xs"
            >
              <X className="h-3 w-3" />
              مسح
            </Button>
          )}
        </div>
      </div>

      <div className="px-4 sm:px-6 py-3 border-b border-border bg-accent/20 flex flex-wrap items-center gap-2" data-testid="coupon-filter-bar">
        <span className="text-xs font-bold text-foreground/70 ml-2">فلتر كود الخصم:</span>
        {[
          { v: 'all',  label: 'الكل' },
          { v: 'any',  label: 'أي خصم' },
          ...(couponChips || []),
          { v: 'none', label: 'بدون خصم' },
        ].map((opt) => (
          <button
            key={opt.v}
            onClick={() => setCouponFilter(opt.v)}
            data-testid={`coupon-filter-${opt.v}`}
            className={`px-3 py-1 rounded-full text-xs font-bold border transition-all font-numbers ${
              couponFilter === opt.v
                ? 'bg-brand_blue text-white border-brand_blue shadow-sm'
                : 'bg-background text-foreground/70 border-foreground/20 hover:border-brand_blue/40 hover:text-brand_blue'
            }`}
          >
            {opt.label}
          </button>
        ))}
        <span className="ms-auto text-xs text-muted-foreground font-numbers" data-testid="coupon-filter-count">
          {filteredSales.length} / {salesTotal}
        </span>
      </div>
      {/* ── Mobile view (< md): stacked cards. Fits screen width, no horizontal scroll. ── */}
      <div className="md:hidden divide-y divide-border" data-testid="sales-mobile-list">
        {filteredSales.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground text-sm">لا توجد مبيعات</div>
        ) : filteredSales.map((sale) => {
          const canEdit = !isEmployee || (isEmployee && sale.employee_id === myEmpId);
          return (
            <div key={sale.id} className="p-3 space-y-2" data-testid={`sale-card-${sale.id}`}>
              {/* Row 1: date + time + amount + inline actions */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex flex-col leading-tight">
                  <span className="text-[11px] text-foreground/60 font-numbers">
                    {new Date(sale.date).toLocaleDateString('ar-SA', { numberingSystem: 'latn', calendar: 'gregory' })}
                  </span>
                  <span className="text-[10px] text-foreground/50 font-numbers" data-testid={`sale-time-mobile-${sale.id}`}>
                    {new Date(sale.date).toLocaleTimeString('ar-SA', { numberingSystem: 'latn', hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Riyadh' })}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-sm font-black text-success font-numbers">
                    {sale.amount.toLocaleString('en-US')} <SAR />
                  </span>
                  {canEdit && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(sale)}
                        data-testid={`edit-sale-mobile-${sale.id}`}
                        className="h-7 w-7 p-0 text-brand_blue"
                        title="تعديل"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDelete(sale.id)}
                        data-testid={`delete-sale-mobile-${sale.id}`}
                        className="h-7 w-7 p-0 text-destructive"
                        title="حذف"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Row 2: product + qty × price */}
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-bold text-foreground truncate">
                  {sale.product_name}
                  {sale.price_option_label && <span className="text-xs font-normal text-primary"> ({sale.price_option_label})</span>}
                </span>
                <span className="text-[11px] text-foreground/60 font-numbers whitespace-nowrap">
                  {sale.quantity || 1} × {(sale.unit_price ?? 0).toLocaleString('en-US')}
                </span>
              </div>

              {/* Row 3: employee (for managers) */}
              {!isEmployee && (
                <div className="text-[11px] text-foreground/70">
                  <span className="text-foreground/50">الموظف:</span> {sale.employee_name}
                </div>
              )}

              {/* Row 4: chips for country + discount + activity + customer */}
              <div className="flex flex-wrap gap-1.5 text-[10px]">
                {sale.country && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-bold">
                    <Globe className="h-2.5 w-2.5" />
                    {sale.country}
                  </span>
                )}
                {sale.used_discount_code && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-brand_blue/10 text-brand_blue font-bold">
                    <Tag className="h-2.5 w-2.5" />
                    {labelFor(sale.discount_pct)}
                  </span>
                )}
                {sale.customer_name && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-accent text-foreground/70 truncate max-w-[45%]">
                    👤 {sale.customer_name}
                  </span>
                )}
                {sale.customer_phone && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-accent text-foreground/70 font-numbers">
                    📞 {sale.customer_phone}
                  </span>
                )}
                {sale.activity_type && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-accent text-foreground/70 truncate max-w-[45%]">
                    🏷 {sale.activity_type}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Desktop / tablet view (md+): full table. ── */}
      <div className="overflow-x-auto hidden md:block">
        <table className="w-full">
          <thead className="bg-accent/50">
            <tr>
              <th className="text-right py-4 px-6 text-sm font-bold text-foreground">التاريخ</th>
              <th className="text-right py-4 px-6 text-sm font-bold text-foreground">الوقت</th>
              <th className="text-right py-4 px-6 text-sm font-bold text-foreground">الموظف</th>
              <th className="text-right py-4 px-6 text-sm font-bold text-foreground">المنتج</th>
              <th className="text-right py-4 px-6 text-sm font-bold text-foreground">الكمية</th>
              <th className="text-right py-4 px-6 text-sm font-bold text-foreground">السعر</th>
              <th className="text-right py-4 px-6 text-sm font-bold text-foreground">الإجمالي</th>
              <th className="text-right py-4 px-6 text-sm font-bold text-foreground">اسم العميل</th>
              <th className="text-right py-4 px-6 text-sm font-bold text-foreground">رقم التواصل</th>
              <th className="text-right py-4 px-6 text-sm font-bold text-foreground">نوع النشاط</th>
              <th className="text-right py-4 px-6 text-sm font-bold text-foreground">الدولة</th>
              <th className="text-right py-4 px-6 text-sm font-bold text-foreground">كود الخصم</th>
              <th className="text-right py-4 px-6 text-sm font-bold text-foreground">إجراء</th>
            </tr>
          </thead>
          <tbody>
            {filteredSales.length === 0 ? (
              <tr>
                <td colSpan="13" className="text-center py-12 text-muted-foreground">لا توجد مبيعات</td>
              </tr>
            ) : filteredSales.map((sale) => (
              <tr key={sale.id} className="border-t border-border hover:bg-accent/50 transition-colors">
                <td className="py-4 px-6 text-foreground">{new Date(sale.date).toLocaleDateString('ar-SA', { numberingSystem: 'latn', calendar: 'gregory' })}</td>
                <td className="py-4 px-6 text-foreground/70 font-numbers text-sm" data-testid={`sale-time-${sale.id}`}>
                  {new Date(sale.date).toLocaleTimeString('ar-SA', { numberingSystem: 'latn', hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Riyadh' })}
                </td>
                <td className="py-4 px-6 text-foreground">{sale.employee_name}</td>
                <td className="py-4 px-6 text-foreground">
                  {sale.product_name}
                  {sale.price_option_label && <span className="block text-xs text-primary" data-testid={`sale-option-label-${sale.id}`}>{sale.price_option_label}</span>}
                </td>
                <td className="py-4 px-6 text-foreground font-numbers">{sale.quantity || 1}</td>
                <td className="py-4 px-6 text-foreground font-numbers">{(sale.unit_price ?? 0).toLocaleString('en-US')}</td>
                <td className="py-4 px-6 text-success font-bold font-numbers">{sale.amount.toLocaleString('en-US')} <SAR /></td>
                <td className="py-4 px-6 text-sm text-foreground/70 max-w-[180px] truncate" title={sale.customer_name || ''} data-testid={`sale-customer-name-${sale.id}`}>
                  {sale.customer_name || <span className="text-foreground/30">—</span>}
                </td>
                <td className="py-4 px-6 text-sm text-foreground/70 font-numbers" data-testid={`sale-customer-phone-${sale.id}`}>
                  {sale.customer_phone || <span className="text-foreground/30">—</span>}
                </td>
                <td className="py-4 px-6 text-sm text-foreground/70 max-w-[160px] truncate" title={sale.activity_type || ''} data-testid={`sale-activity-type-${sale.id}`}>
                  {sale.activity_type || <span className="text-foreground/30">—</span>}
                </td>
                <td className="py-4 px-6 text-sm" data-testid={`sale-country-${sale.id}`}>
                  {sale.country ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 font-bold text-xs">
                      <Globe className="h-3 w-3" />
                      {sale.country}
                    </span>
                  ) : <span className="text-foreground/30">—</span>}
                </td>
                <td className="py-4 px-6 text-sm" data-testid={`sale-discount-code-${sale.id}`}>
                  {sale.used_discount_code ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-brand_blue/10 text-brand_blue font-bold text-xs">
                      <Tag className="h-3 w-3" />
                      {labelFor(sale.discount_pct)}
                    </span>
                  ) : <span className="text-foreground/30">—</span>}
                </td>
                <td className="py-4 px-6">
                  {(() => {
                    const canEdit = !isEmployee || (isEmployee && sale.employee_id === myEmpId);
                    if (!canEdit) {
                      return <span className="text-muted-foreground text-xs">—</span>;
                    }
                    return (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEdit(sale)}
                          data-testid={`edit-sale-${sale.id}`}
                          className="h-8 w-8 p-0 text-brand_blue hover:text-brand_blue"
                          title="تعديل"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDelete(sale.id)}
                          data-testid={`delete-sale-${sale.id}`}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          title="حذف"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
});

const emptyRow = (defaultEmp = '') => ({
  employee_id: defaultEmp,
  product_id: '',
  quantity: 1,
  unit_price: '',
  amount: '',  customer_name: '',
  customer_phone: '',
  activity_type: '',
  country: '',
  used_discount_code: false,
  discount_pct: null,
  price_option_label: '',
  date: saudiToday(),
});

export default function Sales() {
  const { user, employee } = useAuth();
  const isEmployee = user?.role === 'employee';
  const myEmpId = user?.employee_id || '';

  const [sales, setSales] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [products, setProducts] = useState([]);
  const [countries, setCountries] = useState(FALLBACK_COUNTRIES.map((n) => ({ id: n, name: n })));
  const [loading, setLoading] = useState(true);

  // Single sale dialog
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(emptyRow(isEmployee ? myEmpId : ''));
  const [editingId, setEditingId] = useState(null);

  // Bulk sales dialog
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState([emptyRow(isEmployee ? myEmpId : '')]);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  // Dynamic mandatory-field rules (configured by super-admin)
  const [fieldRules, setFieldRules] = useState([]);

  // Coupon filter for the sales table
  const [couponFilter, setCouponFilter] = useState('all'); // 'all' | 'any' | '10' | '15' | ... | 'none'
  // Dynamic discount coupons loaded from /api/coupons so Super Admin can add
  // or retire coupons without touching code. Falls back to COUPON_VALUES.
  const [coupons, setCoupons] = useState([]);
  const [searchQuery, setSearchQuery] = useState(''); // matches employee_name OR product_name
  const [dateFrom, setDateFrom] = useState(''); // YYYY-MM-DD, inclusive
  const [dateTo, setDateTo] = useState('');     // YYYY-MM-DD, inclusive

  const filteredSales = React.useMemo(() => {
    let out = sales;

    // Coupon filter
    if (couponFilter === 'none') out = out.filter((s) => !s.used_discount_code);
    else if (couponFilter === 'any') out = out.filter((s) => !!s.used_discount_code);
    else if (couponFilter !== 'all') {
      const pct = parseInt(couponFilter, 10);
      out = out.filter((s) => s.used_discount_code && s.discount_pct === pct);
    }

    // Text search — employee_name OR product_name (case-insensitive Arabic-friendly substring)
    const q = searchQuery.trim();
    if (q) {
      const qLower = q.toLowerCase();
      out = out.filter((s) =>
        (s.employee_name || '').toLowerCase().includes(qLower) ||
        (s.product_name || '').toLowerCase().includes(qLower)
      );
    }

    // Date range
    if (dateFrom) out = out.filter((s) => (s.date || '').slice(0, 10) >= dateFrom);
    if (dateTo)   out = out.filter((s) => (s.date || '').slice(0, 10) <= dateTo);

    return out;
  }, [sales, couponFilter, searchQuery, dateFrom, dateTo]);

  useEffect(() => {
    fetchData();
    // Load active rules in parallel; failure shouldn't block the page
    axios
      .get(`${API_URL}/api/field-rules/active`)
      .then((r) => setFieldRules(r.data || []))
      .catch(() => setFieldRules([]));
    // Load dynamic discount coupons. Request includes INACTIVE ones so the
    // filter bar can still slice historical sales by a retired coupon. The
    // sale-creation forms separately pick only the active subset.
    axios
      .get(`${API_URL}/api/coupons?include_inactive=1`)
      .then((r) => setCoupons(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCoupons([]));
  }, []);

  // Set of valid pct values (DB active ∪ hardcoded fallback) — used by
  // client-side validation before POST/PUT to the backend.
  const validPctSet = React.useMemo(() => {
    const s = new Set(COUPON_VALUES);
    coupons.forEach((c) => { if (c.active) s.add(Number(c.pct)); });
    return s;
  }, [coupons]);

  // Coupon buttons rendered in the single + bulk sale forms. When DB coupons
  // have loaded we trust the server (retired coupons disappear, new ones show
  // up). Before load, the legacy hardcoded set keeps the form usable.
  const activeCoupons = React.useMemo(() => {
    if (coupons && coupons.length) {
      return coupons.filter((c) => c.active).map((c) => ({ pct: Number(c.pct), label: c.label }));
    }
    return COUPON_VALUES.map((pct) => ({ pct, label: couponLabel(pct) }));
  }, [coupons]);

  // Hoisted ABOVE any early returns to satisfy react-hooks/rules-of-hooks —
  // CRA's production build (CI=true) treats a conditional useMemo as a hard
  // error. Passed as a stable reference to the memoized <SalesTables />.
  // Includes INACTIVE coupons too: historical sales under a retired coupon
  // need to remain filterable even after a super-admin disabled it.
  const couponChips = React.useMemo(() => {
    const src = coupons && coupons.length
      ? coupons.map((c) => ({
          pct: Number(c.pct),
          label: c.active ? c.label : `${c.label} (معطّل)`,
        }))
      : COUPON_VALUES.map((pct) => ({ pct, label: couponLabel(pct) }));
    return src.map(({ pct, label }) => ({ v: String(pct), label }));
  }, [coupons]);

  // Map<pct → coupon label> fed to <SalesTables />. Rebuilt whenever the
  // coupon list changes. Historical sales keep displaying under their current
  // coupon label even if the sentinel number was recycled by a different
  // coupon (shouldn't happen but defensive).
  const couponLabelMap = React.useMemo(() => buildCouponLabelMap(coupons), [coupons]);

  useEffect(() => {
    if (isEmployee && myEmpId && !formData.employee_id) {
      setFormData((prev) => ({ ...prev, employee_id: myEmpId }));
    }
  }, [myEmpId, isEmployee]); // eslint-disable-line

  const fetchData = async () => {
    try {
      const [salesRes, employeesRes, productsRes, countriesRes] = await Promise.all([
        axios.get(`${API_URL}/api/sales`),
        axios.get(`${API_URL}/api/employees`),
        axios.get(`${API_URL}/api/products`),
        axios.get(`${API_URL}/api/countries`).catch(() => ({ data: null })),
      ]);
      setSales(salesRes.data);
      setEmployees(employeesRes.data);
      setProducts(productsRes.data);
      if (countriesRes.data && countriesRes.data.length > 0) {
        setCountries(countriesRes.data);
      }
    } catch (error) {
      toast.error('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const productById = React.useCallback(
    (id) => products.find((p) => p.id === id),
    [products],
  );

  // ─── Perf: precompute a productId → Set<requiredField> map ────────────────
  // The keystroke lag on the sale form came from re-running `requiredFieldsFor`
  // (which loops all fieldRules + arrays + builds a new Set) 7+ times per
  // render — for every character the user typed. Memoizing this into a Map
  // lookup means each keystroke pays O(1) instead of O(products × rules).
  const productMap = React.useMemo(() => {
    const m = new Map();
    for (const p of products) m.set(p.id, p);
    return m;
  }, [products]);

  const requiredFieldsByProduct = React.useMemo(() => {
    const out = new Map();
    for (const p of products) {
      const masterId = p.master_id || null;
      const explicitRuleId = p.field_rule_id || null;
      const set = new Set();
      for (const r of fieldRules) {
        if (!r.active) continue;
        const masters = Array.isArray(r.master_ids) ? r.master_ids : [];
        const matches =
          (masterId && masters.includes(masterId)) ||
          (explicitRuleId && r.id === explicitRuleId);
        if (matches) {
          for (const f of (r.required_fields || [])) set.add(f);
        }
      }
      out.set(p.id, set);
    }
    return out;
  }, [products, fieldRules]);

  const EMPTY_SET = React.useRef(new Set()).current;
  const requiredFieldsFor = React.useCallback(
    (productId) => requiredFieldsByProduct.get(productId) || EMPTY_SET,
    [requiredFieldsByProduct, EMPTY_SET],
  );
  const requiresField = React.useCallback(
    (productId, key) => requiredFieldsFor(productId).has(key),
    [requiredFieldsFor],
  );
  const isIntlProduct = React.useCallback(
    (productId) => requiresField(productId, 'country'),
    [requiresField],
  );
  const isEsmaizProduct = React.useCallback(
    (productId) => {
      const s = requiredFieldsFor(productId);
      return s.has('customer_name') || s.has('customer_phone') || s.has('activity_type');
    },
    [requiredFieldsFor],
  );

  const handleProductChange = React.useCallback((value, setRow) => {
    const p = productById(value);
    setRow((r) => ({
      ...r,
      product_id: value,
      unit_price: p && !hasPriceOptions(p) ? (p.price ?? 0).toString() : '',
      amount: '',
      price_option_label: '',
    }));
  }, [productById]);

  // Stable onChange handlers for the single-form heavy fields (country/qty)
  // so their memoized children don't re-render on every keystroke of another field.
  const handleQtyChange = React.useCallback((q) => {
    setFormData((prev) => ({ ...prev, quantity: q, amount: computeAmount(prev.unit_price, q).toString() }));
  }, []);
  const handleCountryChange = React.useCallback((v) => {
    setFormData((prev) => ({ ...prev, country: v }));
  }, []);

  const computeAmount = (unit, qty) => {
    const u = parseFloat(unit) || 0;
    const q = parseInt(qty, 10) || 0;
    return u * q;
  };

  const FIELD_LABELS_AR = {
    country: 'الدولة',
    customer_name: 'اسم العميل',
    customer_phone: 'رقم التواصل',
    activity_type: 'نوع النشاط',
  };

  // Returns array of missing-field labels for a sale row given product rules.
  const missingRequiredFields = (productId, row) => {
    const need = requiredFieldsFor(productId);
    const miss = [];
    for (const f of need) {
      if (!(row[f] || '').toString().trim()) miss.push(FIELD_LABELS_AR[f] || f);
    }
    return miss;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (hasPriceOptions(productById(formData.product_id)) && !formData.price_option_label) {
      toast.error('اختر الصنف / السعر للمنتج');
      return;
    }
    const miss = missingRequiredFields(formData.product_id, formData);
    if (miss.length) {
      toast.error(`الحقول الإلزامية لهذا المنتج: ${miss.join('، ')}`);
      return;
    }
    if (formData.used_discount_code && !validPctSet.has(formData.discount_pct)) {
      toast.error('عند تفعيل كود الخصم يجب اختيار نسبة (10% أو 15% أو 25% أو 50% أو ND96)');
      return;
    }
    try {
      const payload = {
        employee_id: formData.employee_id,
        product_id: formData.product_id,
        quantity: parseInt(formData.quantity, 10) || 1,
        unit_price: parseFloat(formData.unit_price) || 0,
        amount: parseFloat(formData.amount) || computeAmount(formData.unit_price, formData.quantity),
        date: saudiDateToUtcNoon(formData.date),
        customer_name: (formData.customer_name || '').trim() || null,
        customer_phone: (formData.customer_phone || '').trim() || null,
        activity_type: (formData.activity_type || '').trim() || null,
        country: (formData.country || '').trim() || null,
        used_discount_code: !!formData.used_discount_code,
        discount_pct: formData.used_discount_code ? formData.discount_pct : null,
        price_option_label: hasPriceOptions(productById(formData.product_id)) ? (formData.price_option_label || null) : null,
      };
      if (editingId) {
        await axios.put(`${API_URL}/api/sales/${editingId}`, payload);
        toast.success('تم تعديل المبيعات بنجاح');
      } else {
        await axios.post(`${API_URL}/api/sales`, payload);
        toast.success('تم إضافة المبيعات بنجاح');
      }
      setOpen(false);
      setEditingId(null);
      setFormData(emptyRow(isEmployee ? myEmpId : ''));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل حفظ المبيعات');
    }
  };

  // useCallback so <SalesTables /> (React.memo) keeps stable prop references
  // and never re-renders while the user types in the sale dialog.
  const handleEdit = React.useCallback((sale) => {
    setEditingId(sale.id);
    setFormData({
      employee_id: sale.employee_id || '',
      product_id: sale.product_id || '',
      quantity: sale.quantity ?? 1,
      unit_price: (sale.unit_price ?? 0).toString(),
      amount: (sale.amount ?? 0).toString(),
      customer_name: sale.customer_name || '',
      customer_phone: sale.customer_phone || '',
      activity_type: sale.activity_type || '',
      country: sale.country || '',
      used_discount_code: !!sale.used_discount_code,
      discount_pct: sale.discount_pct ?? null,
      price_option_label: sale.price_option_label || '',
      date: (sale.date || new Date().toISOString()).split('T')[0],
    });
    setOpen(true);
  }, []);

  const handleDelete = React.useCallback(async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه المبيعات؟')) return;
    try {
      await axios.delete(`${API_URL}/api/sales/${id}`);
      toast.success('تم حذف المبيعات بنجاح');
      fetchData();
    } catch (error) {
      toast.error('فشل حذف المبيعات');
    }
  }, []);

  // Bulk handlers
  const addBulkRow = () => setBulkRows((rows) => [...rows, emptyRow(isEmployee ? myEmpId : '')]);
  const removeBulkRow = (idx) => setBulkRows((rows) => rows.length > 1 ? rows.filter((_, i) => i !== idx) : rows);
  const updateBulkRow = (idx, patch) => setBulkRows((rows) => rows.map((r, i) => i === idx ? { ...r, ...patch } : r));

  const handleBulkProductChange = (idx, value) => {
    const p = productById(value);
    updateBulkRow(idx, { product_id: value, unit_price: p && !hasPriceOptions(p) ? (p.price ?? 0).toString() : '', amount: '', price_option_labels: [] });
  };

  const toggleBulkOption = (idx, opt) => setBulkRows((rows) => rows.map((r, i) => {
    if (i !== idx) return r;
    const cur = r.price_option_labels || [];
    const labels = cur.includes(opt.label) ? cur.filter((l) => l !== opt.label) : [...cur, opt.label];
    const opts = (productById(r.product_id)?.price_options || []).filter((o) => labels.includes(o.label));
    const unit = opts.reduce((s, o) => s + (Number(o.price) || 0), 0);
    return { ...r, price_option_labels: labels, unit_price: labels.length ? String(+unit.toFixed(2)) : '', amount: computeAmount(unit, r.quantity).toString() };
  }));

  // A row with several selected options becomes one sale per option (same quantity).
  const expandBulkRow = (r) => {
    const p = productById(r.product_id);
    const qty = parseInt(r.quantity, 10) || 1;
    const base = {
      employee_id: r.employee_id,
      product_id: r.product_id,
      quantity: qty,
      date: saudiDateToUtcNoon(r.date),
      customer_name: (r.customer_name || '').trim() || null,
      customer_phone: (r.customer_phone || '').trim() || null,
      activity_type: (r.activity_type || '').trim() || null,
      country: (r.country || '').trim() || null,
      used_discount_code: !!r.used_discount_code,
      discount_pct: r.used_discount_code ? r.discount_pct : null,
    };
    const single = {
      ...base,
      unit_price: parseFloat(r.unit_price) || 0,
      amount: parseFloat(r.amount) || computeAmount(r.unit_price, r.quantity),
    };
    if (!hasPriceOptions(p)) return [{ ...single, price_option_label: null }];
    const labels = r.price_option_labels || [];
    if (labels.length === 1) return [{ ...single, price_option_label: labels[0] }];
    return p.price_options.filter((o) => labels.includes(o.label)).map((o) => ({
      ...base, unit_price: Number(o.price) || 0, amount: computeAmount(o.price, qty), price_option_label: o.label,
    }));
  };
  const bulkItemsCount = bulkRows.reduce((n, r) => n + Math.max(1, hasPriceOptions(productById(r.product_id)) ? (r.price_option_labels || []).length : 1), 0);

  const handleBulkSubmit = async () => {
    // Validate
    for (const r of bulkRows) {
      if (!r.employee_id || !r.product_id || !r.date) {
        toast.error('يرجى تعبئة جميع الحقول المطلوبة في كل صف');
        return;
      }
      if (r.used_discount_code && !validPctSet.has(r.discount_pct)) {
        toast.error('عند تفعيل كود الخصم يجب اختيار نسبة (10% أو 15% أو 25% أو 50% أو ND96) في كل صف');
        return;
      }
      if (hasPriceOptions(productById(r.product_id)) && !(r.price_option_labels || []).length) {
        toast.error(`اختر الصنف / السعر للمنتج «${productById(r.product_id)?.name || ''}»`);
        return;
      }
      const miss = missingRequiredFields(r.product_id, r);
      if (miss.length) {
        const p = productById(r.product_id);
        toast.error(`المنتج «${p?.name || ''}» يتطلب: ${miss.join('، ')}`);
        return;
      }
    }
    setBulkSubmitting(true);
    try {
      const payload = {
        sales: bulkRows.flatMap(expandBulkRow),
      };
      const res = await axios.post(`${API_URL}/api/sales/bulk`, payload);
      toast.success(`تم إضافة ${res.data.count} مبيعات بنجاح`);
      setBulkOpen(false);
      setBulkRows([emptyRow(isEmployee ? myEmpId : '')]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل حفظ المبيعات');
    } finally {
      setBulkSubmitting(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">جاري التحميل...</div>;
  }

  return (
    <div className="space-y-6" data-testid="sales-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-primary">
            {isEmployee ? 'تسجيل مبيعات جديدة' : 'إدارة المبيعات'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isEmployee ? 'سجل مبيعاتك الجديدة وتابعها' : 'سجل وتتبع المبيعات'}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {/* Edit dialog (opened programmatically from handleEdit; no trigger button shown) */}
          <Dialog open={open} onOpenChange={(isOpen) => {
            setOpen(isOpen);
            if (!isOpen) {
              setEditingId(null);
              setFormData(emptyRow(isEmployee ? myEmpId : ''));
            }
          }}>
            <DialogContent className="w-full sm:w-[calc(100%-1rem)] sm:max-w-[520px] p-0 gap-0 h-[100dvh] sm:h-auto sm:max-h-[92vh] flex flex-col rounded-none sm:rounded-lg top-0 sm:top-[50%] translate-y-0 sm:translate-y-[-50%]" dir="rtl">
              <DialogHeader className="px-4 sm:px-6 pt-5 pb-3 border-b text-right">
                <DialogTitle>{editingId ? 'تعديل مبيعات' : 'إضافة مبيعات جديدة'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
                <div className="space-y-2">
                  <Label>الموظف</Label>
                  {isEmployee ? (
                    <Input
                      data-testid="employee-locked-input"
                      value={employee?.name || user?.name || ''}
                      readOnly
                      disabled
                      className="text-right bg-muted"
                    />
                  ) : (
                    <Select
                      value={formData.employee_id}
                      onValueChange={(v) => setFormData({ ...formData, employee_id: v })}
                      required
                    >
                      <SelectTrigger data-testid="employee-select" className="text-right">
                        <SelectValue placeholder="اختر موظف" />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map((emp) => (
                          <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>المنتج</Label>
                  <Select
                    value={formData.product_id}
                    onValueChange={(v) => handleProductChange(v, setFormData)}
                    required
                  >
                    <SelectTrigger data-testid="product-select" className="text-right">
                      <SelectValue placeholder="اختر منتج" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((prod) => (
                        <SelectItem key={prod.id} value={prod.id}>
                          {prod.name} — {hasPriceOptions(prod) ? 'خيارات متعددة' : <>{(prod.price ?? 0).toLocaleString('en-US')} <SAR /></>}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <PriceOptionPicker
                  small
                  product={productById(formData.product_id)}
                  value={formData.price_option_label}
                  testId="price-option-picker"
                  onSelect={(o) => setFormData((prev) => ({
                    ...prev,
                    price_option_label: o.label,
                    unit_price: String(o.price ?? 0),
                    amount: computeAmount(o.price, prev.quantity).toString(),
                  }))}
                />

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>الكمية</Label>
                    <QuantityPicker
                      testId="quantity-input"
                      value={formData.quantity}
                      onChange={handleQtyChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>سعر الوحدة <SAR /></Label>
                    <Input
                      data-testid="unit-price-input"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.unit_price}
                      onChange={(e) => {
                        const u = e.target.value;
                        const newAmount = computeAmount(u, formData.quantity);
                        setFormData({ ...formData, unit_price: u, amount: newAmount.toString() });
                      }}
                      className="text-right font-numbers"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>المبلغ الإجمالي <SAR /></Label>
                  <Input
                    data-testid="amount-input"
                    type="number"
                    step="0.01"
                    value={formData.amount || computeAmount(formData.unit_price, formData.quantity)}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="text-right font-numbers text-lg h-12"
                    required
                  />
                  <p className="text-xs text-muted-foreground">يُحسب تلقائياً = السعر × الكمية، ويمكنك تعديله يدوياً</p>
                </div>

                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, used_discount_code: !formData.used_discount_code, discount_pct: formData.used_discount_code ? null : formData.discount_pct })}
                    data-testid="toggle-discount-code-button"
                    className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-md border transition-colors shadow-sm ${
                      formData.used_discount_code
                        ? 'border-brand_blue bg-brand_blue/10 text-brand_blue'
                        : 'border-input bg-accent text-accent-foreground'
                    }`}
                    aria-pressed={formData.used_discount_code}
                  >
                    <span className="flex items-center gap-2 font-bold text-sm">
                      <Tag className="h-5 w-5" />
                      {formData.used_discount_code ? 'تم البيع بكود خصم ✓' : 'اضغط هنا إذا استخدمت كوبون خصم'}
                    </span>
                    <span className={`h-5 w-9 rounded-full relative transition-colors ${formData.used_discount_code ? 'bg-brand_blue' : 'bg-foreground/20'}`}>
                      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${formData.used_discount_code ? 'right-0.5' : 'right-[18px]'}`} />
                    </span>
                  </button>
                  {formData.used_discount_code && (
                    <div className="grid grid-cols-5 gap-2" data-testid="discount-pct-group">
                      {activeCoupons.map(({ pct, label }) => {
                        const active = formData.discount_pct === pct;
                        return (
                          <button
                            key={pct}
                            type="button"
                            onClick={() => setFormData({ ...formData, discount_pct: active ? null : pct })}
                            data-testid={`discount-pct-${pct}`}
                            className={`py-2 rounded-md border-2 font-black text-sm font-numbers transition-all ${
                              active
                                ? 'border-brand_blue bg-brand_blue text-white shadow-sm'
                                : 'border-brand_blue/40 bg-brand_blue/5 text-brand_blue hover:bg-brand_blue/15'
                            }`}
                            aria-pressed={active}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-foreground/70">التاريخ</Label>
                  <Input
                    data-testid="date-input"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="text-right text-sm h-9 max-w-[180px] font-numbers"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>
                      اسم العميل{' '}
                      {isEsmaizProduct(formData.product_id) ? (
                        <span className="text-xs font-bold text-destructive">(إلزامي *)</span>
                      ) : (
                        <span className="text-xs text-foreground/50">(اختياري)</span>
                      )}
                    </Label>
                    <Input
                      data-testid="customer-name-input"
                      type="text"
                      value={formData.customer_name}
                      onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                      placeholder="مثال: أحمد العتيبي"
                      className={`text-right ${
                        isEsmaizProduct(formData.product_id) && !(formData.customer_name || '').trim()
                          ? 'border-destructive ring-1 ring-destructive/40'
                          : ''
                      }`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>
                      رقم التواصل{' '}
                      {isEsmaizProduct(formData.product_id) ? (
                        <span className="text-xs font-bold text-destructive">(إلزامي *)</span>
                      ) : (
                        <span className="text-xs text-foreground/50">(اختياري)</span>
                      )}
                    </Label>
                    <Input
                      data-testid="customer-phone-input"
                      type="tel"
                      inputMode="tel"
                      value={formData.customer_phone}
                      onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                      placeholder="05xxxxxxxx"
                      className={`text-right font-numbers ${
                        isEsmaizProduct(formData.product_id) && !(formData.customer_phone || '').trim()
                          ? 'border-destructive ring-1 ring-destructive/40'
                          : ''
                      }`}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>
                    نوع النشاط{' '}
                    {isEsmaizProduct(formData.product_id) ? (
                      <span className="text-xs font-bold text-destructive">(إلزامي *)</span>
                    ) : (
                      <span className="text-xs text-foreground/50">(اختياري)</span>
                    )}
                  </Label>
                  <Input
                    data-testid="activity-type-input"
                    type="text"
                    value={formData.activity_type}
                    onChange={(e) => setFormData({ ...formData, activity_type: e.target.value })}
                    placeholder="مثال: تجارة، مطاعم، مقاولات..."
                    className={`text-right ${
                      isEsmaizProduct(formData.product_id) && !(formData.activity_type || '').trim()
                        ? 'border-destructive ring-1 ring-destructive/40'
                        : ''
                    }`}
                  />
                  {isEsmaizProduct(formData.product_id) && (
                    <p className="text-[11px] text-destructive font-bold" data-testid="esmaiz-hint">
                      هذا منتج اسميز — اسم العميل ورقم التواصل ونوع النشاط إلزامية قبل الحفظ
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-brand_blue" />
                    الدولة{' '}
                    {isIntlProduct(formData.product_id) ? (
                      <span className="text-xs font-bold text-destructive">(إلزامي *)</span>
                    ) : (
                      <span className="text-xs text-foreground/50">(اختياري)</span>
                    )}
                  </Label>
                  <SearchableCountrySelect
                    countries={countries}
                    value={formData.country || ''}
                    onChange={handleCountryChange}
                    error={isIntlProduct(formData.product_id) && !(formData.country || '').trim()}
                    testId="country-select"
                  />
                  {isIntlProduct(formData.product_id) && (
                    <p className="text-[11px] text-destructive font-bold" data-testid="intl-country-hint">
                      هذا منتج دولي — اختيار الدولة إلزامي قبل الحفظ
                    </p>
                  )}
                </div>
                </div>

                <div className="border-t px-4 sm:px-6 py-3 pb-[max(env(safe-area-inset-bottom),12px)] flex flex-col-reverse sm:flex-row gap-2 sm:justify-end bg-background mt-auto shrink-0" data-testid="single-sale-footer">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)} className="w-full sm:w-auto">إلغاء</Button>
                  <Button type="submit" data-testid="save-sale-button" className="w-full sm:w-auto">{editingId ? 'تحديث' : 'حفظ'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Bulk sales */}
          <Dialog open={bulkOpen} onOpenChange={(isOpen) => {
            setBulkOpen(isOpen);
            if (!isOpen) setBulkRows([emptyRow(isEmployee ? myEmpId : '')]);
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-bulk-sale-button" className="gap-2">
                <ListPlus className="h-4 w-4" />
                إضافة مبيعات
              </Button>
            </DialogTrigger>
            <DialogContent className="w-full sm:w-[calc(100%-1rem)] sm:max-w-[900px] p-0 gap-0 h-[100dvh] sm:h-auto sm:max-h-[92vh] flex flex-col rounded-none sm:rounded-lg top-0 sm:top-[50%] translate-y-0 sm:translate-y-[-50%]" dir="rtl">
              <DialogHeader className="px-4 sm:px-6 pt-3 pb-2 border-b text-center">
                <DialogTitle className="text-sm text-primary">يمكنك إضافة منتج أو أكثر</DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto px-3 sm:px-5 py-3">
                <div className="space-y-2">
                {bulkRows.map((row, idx) => (
                <Card key={idx} className="relative p-2.5 pt-7 bg-accent/30 border-border" data-testid={`bulk-row-${idx}`}>
                    {bulkRows.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeBulkRow(idx)}
                        data-testid={`remove-bulk-row-${idx}`}
                        className="absolute top-1 left-1 h-6 px-1.5 gap-1 text-destructive hover:bg-destructive/10"
                      >
                        <X className="h-3.5 w-3.5" />
                        <span className="text-[10px] font-bold">حذف هذا المنتج</span>
                      </Button>
                    )}
                    <div className="grid grid-cols-12 gap-2 items-end">
                      {/* Discount-code toggle — positioned at the TOP of each row, above the date field */}
                      <div className="col-span-12 space-y-1">
                        <button
                          type="button"
                          onClick={() => updateBulkRow(idx, { used_discount_code: !row.used_discount_code, discount_pct: row.used_discount_code ? null : row.discount_pct })}
                          data-testid={`bulk-discount-code-${idx}`}
                          className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md border text-xs font-bold transition-colors shadow-sm ${
                            row.used_discount_code
                              ? 'border-brand_blue bg-brand_blue/10 text-brand_blue'
                              : 'border-input bg-accent text-accent-foreground'
                          }`}
                          aria-pressed={!!row.used_discount_code}
                        >
                          <span className="flex items-center gap-1.5">
                            <Tag className="h-4 w-4" />
                            {row.used_discount_code ? 'تم البيع بكود خصم ✓' : 'اضغط هنا إذا استخدمت كوبون خصم'}
                          </span>
                          <span className={`h-4 w-7 rounded-full relative transition-colors ${row.used_discount_code ? 'bg-brand_blue' : 'bg-foreground/20'}`}>
                            <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-all ${row.used_discount_code ? 'right-0.5' : 'right-[14px]'}`} />
                          </span>
                        </button>
                        {row.used_discount_code && (
                          <div className="grid grid-cols-5 gap-2 mt-2" data-testid={`bulk-discount-pct-${idx}`}>
                            {activeCoupons.map(({ pct, label }) => {
                              const active = row.discount_pct === pct;
                              return (
                                <button
                                  key={pct}
                                  type="button"
                                  onClick={() => updateBulkRow(idx, { discount_pct: active ? null : pct })}
                                  className={`py-1.5 rounded-md border-2 font-black text-xs font-numbers transition-all ${
                                    active
                                      ? 'border-brand_blue bg-brand_blue text-white'
                                      : 'border-brand_blue/40 bg-brand_blue/5 text-brand_blue hover:bg-brand_blue/15'
                                  }`}
                                >
                                  {label}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <div className="col-span-6 md:col-span-2 space-y-1">
                        <Label className="text-sm">الموظف</Label>
                        {isEmployee ? (
                          <Input value={employee?.name || user?.name || ''} readOnly disabled className="text-right h-9 text-sm bg-muted" />
                        ) : (
                          <Select value={row.employee_id} onValueChange={(v) => updateBulkRow(idx, { employee_id: v })}>
                            <SelectTrigger className="text-right h-9 text-sm" data-testid={`bulk-employee-${idx}`}><SelectValue placeholder="موظف" /></SelectTrigger>
                            <SelectContent>
                              {employees.map((emp) => <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                      <div className="col-span-6 md:col-span-2 space-y-1">
                        <Label className="text-sm">المنتج</Label>
                        <Select value={row.product_id} onValueChange={(v) => handleBulkProductChange(idx, v)}>
                          <SelectTrigger className="text-right h-9 text-sm" data-testid={`bulk-product-${idx}`}><SelectValue placeholder="منتج" /></SelectTrigger>
                          <SelectContent>
                            {products.map((p) => <SelectItem key={p.id} value={p.id}>{hasPriceOptions(p) ? p.name : `${p.name} — ${(p.price ?? 0).toLocaleString('en-US')}`}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      {hasPriceOptions(productById(row.product_id)) && (
                        <div className="col-span-12">
                          <PriceOptionPicker
                            small
                            product={productById(row.product_id)}
                            values={row.price_option_labels || []}
                            testId={`bulk-price-option-${idx}`}
                            onSelect={(o) => toggleBulkOption(idx, o)}
                          />
                        </div>
                      )}
                      <div className="col-span-6 md:col-span-3 space-y-1">
                        <Label className="text-sm">الكمية</Label>
                        <QuantityPicker
                          testId={`bulk-quantity-${idx}`}
                          size="sm"
                          value={row.quantity || 1}
                          onChange={(v) => updateBulkRow(idx, { quantity: v, amount: computeAmount(row.unit_price, v).toString() })}
                        />
                      </div>
                      <div className="col-span-6 md:col-span-3 space-y-1">
                        <Label className="text-sm">السعر <span className="text-foreground/50 text-[10px] font-normal">{(row.price_option_labels || []).length > 1 ? '(مجموع الأصناف)' : '(للواحد)'}</span></Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={row.unit_price}
                          readOnly={(row.price_option_labels || []).length > 1}
                          onChange={(e) => {
                            const u = e.target.value;
                            updateBulkRow(idx, { unit_price: u, amount: computeAmount(u, row.quantity).toString() });
                          }}
                          className="text-right h-9 text-sm font-numbers"
                          data-testid={`bulk-unit-price-${idx}`}
                        />
                      </div>
                      <div className="col-span-12 flex gap-2 items-stretch">
                        <div className="flex-1 min-w-0 flex flex-col gap-1">
                          <Label className="text-sm whitespace-nowrap overflow-hidden text-ellipsis block">
                            اسم العميل{' '}
                            {isEsmaizProduct(row.product_id) ? (
                              <span className="text-destructive font-bold">(إلزامي)</span>
                            ) : (
                              <span className="text-foreground/50">(اختياري)</span>
                            )}
                          </Label>
                          <Input
                            data-testid={`bulk-customer-name-${idx}`}
                            value={row.customer_name || ''}
                            onChange={(e) => updateBulkRow(idx, { customer_name: e.target.value })}
                            placeholder="اسم العميل"
                            className={`text-right text-sm w-full !h-9 box-border ${
                              isEsmaizProduct(row.product_id) && !(row.customer_name || '').trim()
                                ? 'border-destructive ring-1 ring-destructive/40'
                                : ''
                            }`}
                          />
                        </div>
                        <div className="w-[44%] shrink-0 flex flex-col gap-1">
                          <Label className="text-sm">التاريخ</Label>
                          <Input
                            type="date"
                            value={row.date}
                            onChange={(e) => updateBulkRow(idx, { date: e.target.value })}
                            className="text-right text-sm px-1.5 font-numbers w-full !h-9 box-border appearance-none"
                            style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
                          />
                        </div>
                      </div>
                      <div className="col-span-6 md:col-span-4 space-y-1">
                        <Label className="text-sm">
                          رقم التواصل{' '}
                          {isEsmaizProduct(row.product_id) ? (
                            <span className="text-destructive font-bold">(إلزامي *)</span>
                          ) : (
                            <span className="text-foreground/50">(اختياري)</span>
                          )}
                        </Label>
                        <Input
                          data-testid={`bulk-customer-phone-${idx}`}
                          type="tel"
                          inputMode="tel"
                          value={row.customer_phone || ''}
                          onChange={(e) => updateBulkRow(idx, { customer_phone: e.target.value })}
                          placeholder="05xxxxxxxx"
                          className={`text-right h-9 text-sm font-numbers ${
                            isEsmaizProduct(row.product_id) && !(row.customer_phone || '').trim()
                              ? 'border-destructive ring-1 ring-destructive/40'
                              : ''
                          }`}
                        />
                      </div>
                      <div className="col-span-6 md:col-span-4 space-y-1">
                        <Label className="text-sm">
                          نوع النشاط{' '}
                          {isEsmaizProduct(row.product_id) ? (
                            <span className="text-destructive font-bold">(إلزامي *)</span>
                          ) : (
                            <span className="text-foreground/50">(اختياري)</span>
                          )}
                        </Label>
                        <Input
                          data-testid={`bulk-activity-type-${idx}`}
                          value={row.activity_type || ''}
                          onChange={(e) => updateBulkRow(idx, { activity_type: e.target.value })}
                          placeholder="تجارة، مطاعم..."
                          className={`text-right h-9 text-sm ${
                            isEsmaizProduct(row.product_id) && !(row.activity_type || '').trim()
                              ? 'border-destructive ring-1 ring-destructive/40'
                              : ''
                          }`}
                        />
                      </div>
                      <div className="col-span-12 md:col-span-12 space-y-1">
                        <Label className="text-sm flex items-center gap-1.5">
                          <Globe className="h-4 w-4 text-brand_blue" />
                          الدولة{' '}
                          {isIntlProduct(row.product_id) ? (
                            <span className="text-destructive font-bold">(إلزامي *)</span>
                          ) : (
                            <span className="text-foreground/50">(اختياري)</span>
                          )}
                        </Label>
                        <SearchableCountrySelect
                          countries={countries}
                          value={row.country || ''}
                          onChange={(v) => updateBulkRow(idx, { country: v })}
                          error={isIntlProduct(row.product_id) && !(row.country || '').trim()}
                          testId={`bulk-country-${idx}`}
                        />
                      </div>
                    </div>
                  </Card>
                ))}

                <Button
                  type="button"
                  variant="outline"
                  onClick={addBulkRow}
                  data-testid="add-bulk-row-button"
                  className="w-full gap-2 h-8 text-sm border-primary text-primary bg-primary/5 hover:bg-primary/15"
                >
                  <PlusCircle className="h-4 w-4" />
                  إضافة منتج آخر / خدمة إضافية
                </Button>
                </div>
              </div>

              <div className="sticky bottom-0 bg-background border-t px-4 sm:px-6 py-2 pb-[max(env(safe-area-inset-bottom),8px)] flex flex-col-reverse sm:flex-row gap-2 sm:justify-end sm:items-center">
                <Button type="button" variant="outline" onClick={() => setBulkOpen(false)} className="w-full sm:w-auto h-8 text-sm">إلغاء</Button>
                <Button
                  onClick={handleBulkSubmit}
                  disabled={bulkSubmitting}
                  data-testid="save-bulk-sales-button"
                  className="w-full sm:w-auto h-8 text-sm gap-2"
                >
                  {bulkSubmitting ? 'جاري الحفظ...' : (() => {
                    const total = bulkRows.reduce((sum, r) => {
                      const a = parseFloat(r.amount);
                      return sum + (Number.isFinite(a) ? a : computeAmount(r.unit_price, r.quantity));
                    }, 0);
                    return (
                      <>
                        <span>{`حفظ ${bulkItemsCount} مبيعات`}</span>
                        {total > 0 && (
                          <span data-testid="bulk-total-amount" className="font-numbers font-bold bg-white/15 px-2 py-0.5 rounded">
                            {total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ر.س
                          </span>
                        )}
                      </>
                    );
                  })()}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <SalesTables
        filteredSales={filteredSales}
        salesTotal={sales.length}
        isEmployee={isEmployee}
        myEmpId={myEmpId}
        onEdit={handleEdit}
        onDelete={handleDelete}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        dateFrom={dateFrom}
        setDateFrom={setDateFrom}
        dateTo={dateTo}
        setDateTo={setDateTo}
        couponFilter={couponFilter}
        setCouponFilter={setCouponFilter}
        couponChips={couponChips}
        couponLabelMap={couponLabelMap}
      />
    </div>
  );
}
